import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getMediaUrl } from '../../../utils/mediaUrl';
import { saveJob, unsaveJob } from '../../../services/api';
import CardIcon from '../../../shared/components/icons/CardIcon';
import { getAuth } from '../../../features/auth/utils/authStorage';

function JobCard({ job, onSave, onApply, linkTo, showActions = true, requireLoginForApply = false }) {
  const [isSaved, setIsSaved] = useState(job?.isSaved || false);
  const [hasApplied, setHasApplied] = useState(job?.hasApplied || false);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);

  const getUrgencyBadge = () => {
    const urgencyMap = {
      urgent: { icon: 'flame', text: 'URGENT', color: 'bg-red-50 text-red-700 border border-red-200' },
      high: { icon: 'flame', text: 'HIGH', color: 'bg-orange-50 text-orange-700 border border-orange-200' },
      normal: { icon: 'check', text: 'NORMAL', color: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
    };
    const urgency = urgencyMap[job?.urgency] || urgencyMap.normal;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-bold rounded ${urgency.color}`}>
        <CardIcon name={urgency.icon} className="h-3.5 w-3.5" />
        {urgency.text}
      </span>
    );
  };

  const handleSaveClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const nextSaved = !isSaved;
    setIsSaved(nextSaved);
    try {
      if (onSave) await onSave(job.id, nextSaved);
      else await (nextSaved ? saveJob(job.id) : unsaveJob(job.id));
    } catch (error) {
      setIsSaved(!nextSaved);
    }
  };

  const handleApplyClick = (e) => {
    e.preventDefault();
    if (requireLoginForApply && !getAuth()?.token) {
      setLoginPromptOpen(true);
      return;
    }
    if (!hasApplied) {
      setHasApplied(true);
      onApply?.(job.id);
    }
  };

  const [avatarVisible, setAvatarVisible] = useState(true);
  const userInitials = `${job?.user?.firstName?.[0] || ''}${job?.user?.lastName?.[0] || ''}`.toUpperCase();
  const userPhoto = getMediaUrl(job?.user?.profilePhoto || job?.user?.profile?.profilePhoto);
  const workTypeLabel = job?.workType?.replace('_', ' ') || 'Full Time';

  // Handle both API job objects and simple prop-based cards
  if (!job?.id) {
    // Fallback for simple card props
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-base font-semibold text-navy">{job?.title}</p>
            <p className="mt-1 text-sm text-slate-600">{job?.location}</p>
            <p className="mt-1 text-xs text-slate-500">{job?.meta}</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-orange">{job?.pay}</p>
            <button
              type="button"
              onClick={handleApplyClick}
              disabled={hasApplied}
              className={`mt-2 rounded-2xl px-4 py-2 text-sm font-semibold transition ${
                hasApplied
                  ? 'bg-slate-100 text-slate-500 cursor-not-allowed'
                  : 'bg-navy text-white hover:bg-slate-800'
              }`}
            >
              {hasApplied ? '✓ Applied' : 'Apply'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Link to={linkTo || `/worker/job/${job.id}`} state={{ job }} className="block">
      <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        {/* Header with urgency badge and save button */}
        <div className="flex items-start justify-between mb-4">
          <div>{getUrgencyBadge()}</div>
          {showActions && <button
            type="button"
            onClick={handleSaveClick}
            className={`text-xl transition-colors duration-200 ${isSaved ? 'text-orange-600' : 'text-slate-400 hover:text-orange-600'}`}
            aria-label={isSaved ? 'Remove from saved' : 'Save job'}
          >
            <CardIcon name={isSaved ? 'heartFilled' : 'heart'} className="h-5 w-5" />
          </button>}
        </div>

        {/* User info with avatar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-sm font-bold">
            {userPhoto && avatarVisible ? (
              <img
                src={userPhoto}
                alt={`${job.user.firstName} avatar`}
                className="h-full w-full object-cover"
                onError={() => setAvatarVisible(false)}
              />
            ) : (
              <span>{userInitials}</span>
            )}
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">
              {job.user?.firstName} {job.user?.lastName}
            </p>
          </div>
        </div>

        {/* Job title */}
        <h3 className="text-lg font-bold text-navy mb-2">{job.title}</h3>

        {/* Job description */}
        <p className="text-sm text-slate-600 mb-4 line-clamp-2">
          {job.description}
        </p>

        {/* Location, Budget, and Workers */}
        <div className="space-y-2 mb-4 text-sm">
          <div className="flex items-center gap-2 text-slate-700">
            <CardIcon name="location" />
            <span>{job.area}{job.city && `, ${job.city}`}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-700">
            <CardIcon name="category" />
            <span className="text-slate-600">{job.skill?.category?.name || job.category || 'Uncategorized'}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-700">
            <CardIcon name="check" />
            <span className="text-slate-600">{job.skill?.name || 'General'}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-700">
            <CardIcon name="money" />
            <span className="font-semibold text-orange-600">K{job.budget}</span>
            {job.isNegotiable && <span className="text-xs text-slate-500">(Negotiable)</span>}
          </div>
          {job.requiredPeople > 1 && (
            <div className="flex items-center gap-2 text-slate-700">
              <CardIcon name="people" />
              <span>Hiring {job.requiredPeople} workers</span>
            </div>
          )}
        </div>

        {/* Work type and posted time */}
        <div className="flex items-center justify-between text-xs text-slate-500 mb-4 pb-4 border-t border-slate-100">
          <span className="inline-block mt-3 px-2 py-1 bg-slate-100 text-slate-700 rounded font-medium">
            {workTypeLabel}
          </span>
          <span className="mt-3">{job.postedAgo || 'Recently posted'}</span>
        </div>

        {/* Actions */}
        {showActions && <div className="flex items-center justify-between gap-3">
          <button
            onClick={handleApplyClick}
            disabled={hasApplied}
            className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-colors duration-200 text-sm ${
              hasApplied
                ? 'bg-slate-100 text-slate-500 cursor-not-allowed'
                : 'bg-navy text-white hover:bg-slate-800'
            }`}
          >
            {hasApplied ? '✓ Applied' : 'Apply'}
          </button>
        </div>}
      </article>
      </Link>
      {loginPromptOpen && (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/50 p-4" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setLoginPromptOpen(false);
        }}>
          <section className="w-full max-w-sm overflow-hidden rounded-2xl border border-amber-200 border-t-4 border-t-amber-400 bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="login-to-apply-title">
            <div className="bg-amber-50/80 p-6">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-amber-100 text-amber-800"><CardIcon name="user" /></span>
              <div>
                <h2 id="login-to-apply-title" className="text-lg font-bold text-navy">Log in to apply</h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">Sign in to submit an application for {job.title || 'this job'}.</p>
              </div>
            </div>
            <div className="mt-6 flex justify-center gap-3">
              <button type="button" onClick={() => setLoginPromptOpen(false)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Not now</button>
              <Link to="/login" state={{ from: linkTo || `/job/${job.id}` }} className="inline-flex items-center gap-2 rounded-xl bg-orange px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-600"><CardIcon name="user" />Log in</Link>
            </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export default JobCard;

